import React from 'react'
import { Link } from 'react-router-dom'

/**
 * Reusable Button component with responsive design
 * Supports different variants, sizes, and states
 */
const Button = ({
  children,
  variant = 'primary',
  size = 'medium',
  disabled = false,
  loading = false,
  fullWidth = false,
  className = '',
  onClick,
  type = 'button',
  // New props for link behavior
  to,
  href,
  target,
  rel,
  ...props
}) => {
  // Base button classes
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'
  
  // Variant classes
  const variantClasses = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
    secondary: 'bg-gray-200 text-gray-900 hover:bg-gray-300 focus:ring-gray-500',
    outline: 'border-2 border-blue-600 text-blue-600 hover:bg-blue-50 focus:ring-blue-500',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
    success: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500',
  }
  
  // Size classes
  const sizeClasses = {
    extraSmall: 'px-2 py-1 text-xs',
    small: 'px-3 py-1 text-sm',
    medium: 'px-4 py-2 text-sm',
    large: 'px-6 py-3 text-base',
  }
  
  // Width classes
  const widthClasses = fullWidth ? 'w-full' : ''
  
  // Combine all classes
  const disabledInteractionClasses = disabled || loading ? ' pointer-events-none' : ''
  const buttonClasses = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${widthClasses} ${className}${disabledInteractionClasses}`

  // Render as Link if `to` is provided (internal navigation)
  // SECURITY: Links cannot be truly disabled, so we prevent interactions via CSS and preventDefault when disabled/loading
  if (to) {
    return (
      <Link
        to={to}
        className={buttonClasses}
        aria-disabled={disabled || loading}
        onClick={(event) => {
          if (disabled || loading) {
            event.preventDefault()
            return
          }
          if (onClick) onClick(event)
        }}
        {...props}
      >
        {loading && (
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        )}
        {children}
      </Link>
    )
  }

  // Render as anchor if `href` is provided (external link or mailto)
  // SECURITY: Add rel="noopener noreferrer" automatically when target is _blank unless caller provides a rel
  if (href) {
    const derivedRel = target === '_blank' ? (rel || 'noopener noreferrer') : rel
    return (
      <a
        href={href}
        target={target}
        rel={derivedRel}
        className={buttonClasses}
        aria-disabled={disabled || loading}
        onClick={(event) => {
          if (disabled || loading) {
            event.preventDefault()
            return
          }
          if (onClick) onClick(event)
        }}
        {...props}
      >
        {loading && (
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        )}
        {children}
      </a>
    )
  }

  return (
    <button
      type={type}
      className={buttonClasses}
      disabled={disabled || loading}
      onClick={onClick}
      {...props}
    >
      {loading && (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      )}
      {children}
    </button>
  )
}

export default Button
